import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Bot,
  AlertCircle,
  CirclePause,
  CheckCircle2,
  GitBranch,
  Clock,
} from 'lucide-react';
import { CardGrid } from '@reflex/ui/components/shared/CardGrid';
import { Card, CardContent, CardHeader } from '@reflex/ui/components/ui/card';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { StatCard } from '@reflex/ui/components/shared/StatCard';
import { PanelTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { AgentActivityChip } from '@/components/status/agent/AgentActivityChip';
import { ActivityDot } from '@/components/status/agent/ActivityDot';
import { AgentStatusChip, AgentStatusDot } from '@/components/status/agent/AgentStatusChip';
import { AgentFilterBar } from '@/components/agent/AgentFilterBar';
import { AdminUsagePanel } from '@/components/monitor/AdminUsagePanel';
import { useAgents } from '@/hooks/agents/useAgents';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { useLiveAgentTurnSignals } from '@/hooks/agents/useLiveAgentTurnSignals';
import { activityFromDurableAgentState } from '@/hooks/agents/agent-activity-utils';
import { useAgentViews } from '@/hooks/agents/useAgentViews';
import { useUsers } from '@/hooks/auth/useUsers';
import { useAuth } from '@/hooks/auth/useAuth';
import { useAgentFilters } from '@/hooks/agents/useAgentFilters';
import { useAgentListCompletion } from '@/hooks/agents/useAgentListCompletion';
import { cn } from '@reflex/ui/lib/utils';
import { getGitRepoAttachment } from '@reflex/shared';
import { timeAgo, formatDurationRange } from '@reflex/ui/lib/format';
import { ACTIVE_STATUSES, TERMINAL_STATUSES } from '@/lib/types';
import type { Agent, AgentActivity, AgentStatus, User } from '@/lib/types';
import { ROUTES } from '@/lib/routes';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { getNonArchivedAgents, getUserDisplayName } from '@/lib/agent-utils';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useUsageSnapshot } from '@/hooks/admin/useUsageSnapshot';

type AgentCategory = 'active' | 'needs_input' | 'errored' | 'completed';

const CATEGORY_STATUSES: Record<AgentCategory, AgentStatus[]> = {
  active: [...ACTIVE_STATUSES],
  needs_input: ['needs_input'],
  errored: ['error'],
  completed: ['completed', 'stopped', 'terminated'],
};

const CATEGORY_LABELS: Record<AgentCategory, string> = {
  active: 'Active Agents',
  needs_input: 'Agents Needing Input',
  errored: 'Errored Agents',
  completed: 'Completed Agents',
};

const CATEGORY_EMPTY: Record<AgentCategory, string> = {
  active: 'No agents running',
  needs_input: 'No agents waiting for input',
  errored: 'No errored agents',
  completed: 'No completed agents',
};

interface UserGroup {
  user: User | null;
  userId: string | null;
  agents: Agent[];
  activeCount: number;
}

function useUserGroups(agents: Agent[], users: User[] | undefined): UserGroup[] {
  return useMemo(() => {
    const userMap = new Map<string, User>();
    for (const u of users ?? []) userMap.set(u.id, u);

    const groups = new Map<string | null, Agent[]>();
    for (const a of agents) {
      const key = a.userId ?? null;
      const list = groups.get(key);
      if (list) list.push(a);
      else groups.set(key, [a]);
    }

    return Array.from(groups.entries())
      .map(([userId, userAgents]) => ({
        user: userId ? (userMap.get(userId) ?? null) : null,
        userId,
        agents: userAgents,
        activeCount: userAgents.filter((a) => ACTIVE_STATUSES.has(a.status)).length,
      }))
      .sort((a, b) => b.activeCount - a.activeCount || b.agents.length - a.agents.length);
  }, [agents, users]);
}

export function MonitorPage() {
  const { user: authUser, isPlatformAdmin } = useAuth();
  // Live read state for the `Unread` facet, so the monitor's `filterAgents`
  // narrows to unread runs the same way the sidebar does.
  const unreadViews = useAgentViews();
  const turnActivities = useLiveAgentTurnSignals();
  const agentFilters = useAgentFilters(authUser?.id ?? null, {
    storageKey: 'monitor-filters',
    defaultUserId: null,
    // The admin monitor surfaces every run — flow- and integration-launched
    // included — so it defaults to all originator kinds, not just agents.
    defaultOriginatorKind: null,
    unreadViews,
    unreadActivities: turnActivities,
  });
  const { snapshot: usageSnapshot } = useUsageSnapshot(isPlatformAdmin);
  const agentsQuery = useAgents(agentFilters.serverFilters);
  const usersQuery = useUsers();
  const agents = agentsQuery.data;
  const users = usersQuery.data;
  const orgRoutes = useOrgRoutes();

  const allNonArchived = useMemo(() => getNonArchivedAgents(agents ?? []), [agents]);
  const nonArchived = useMemo(
    () => agentFilters.filterAgents(allNonArchived),
    [agentFilters, allNonArchived],
  );
  // The monitor has no scroll sentinel either, so a facet the route cannot
  // apply would otherwise be answered from the first page alone.
  useAgentListCompletion({
    enabled: agentFilters.clientFilterActive,
    matchedCount: nonArchived.length,
    pagesLoaded: agentsQuery.pagesLoaded,
    hasNextPage: agentsQuery.hasNextPage,
    isFetchingNextPage: agentsQuery.isFetchingNextPage,
    fetchNextPage: agentsQuery.fetchNextPage,
  });

  const [selectedCategory, setSelectedCategory] = useState<AgentCategory>('active');

  const categoryCounts = useMemo(() => {
    const active = nonArchived.filter((a) => ACTIVE_STATUSES.has(a.status));
    const needsInput = nonArchived.filter((a) => a.status === 'needs_input');
    const errored = nonArchived.filter((a) => a.status === 'error');
    const completed = nonArchived.filter((a) => TERMINAL_STATUSES.has(a.status));
    return { active, needsInput, errored, completed };
  }, [nonArchived]);

  const categoryAgents = useMemo(() => {
    const statuses = CATEGORY_STATUSES[selectedCategory];
    return nonArchived.filter((a) => statuses.includes(a.status));
  }, [nonArchived, selectedCategory]);

  const isActiveCategory = selectedCategory === 'active' || selectedCategory === 'needs_input';
  // Display activity per row, from the durable turn state — the same rule
  // the sidebar uses, so the monitor never disagrees with it. The live
  // `turnActivities` map above feeds only the evidence-gated Unread facet.
  const activities = useMemo(
    () =>
      new Map<string, AgentActivity>(
        nonArchived.map((a) => [a.id, activityFromDurableAgentState(a)]),
      ),
    [nonArchived],
  );

  const displayedAgents = useMemo(() => {
    return [...categoryAgents].sort((a, b) => b.createdAt - a.createdAt);
  }, [categoryAgents]);

  const userMap = useMemo(() => {
    const map = new Map<string, User>();
    for (const u of users ?? []) map.set(u.id, u);
    return map;
  }, [users]);

  const isLoading = agentsQuery.isLoading || usersQuery.isLoading;
  const isError = agentsQuery.isError || usersQuery.isError;

  const userGroups = useUserGroups(nonArchived, users);

  const agentColumns: DataTableColumn<Agent>[] = useMemo(
    () => [
      {
        header: 'Agent',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (agent) => {
          const activity = isActiveCategory ? (activities.get(agent.id) ?? 'working') : null;
          return (
            <Link to={orgRoutes.agent(agent.id)} className="group flex items-center gap-2">
              {activity ? (
                <ActivityDot activity={activity} />
              ) : (
                <AgentStatusDot status={agent.status} />
              )}
              <span className="max-w-60 truncate font-medium group-hover:underline">
                {agent.name}
              </span>
            </Link>
          );
        },
      },
      {
        header: 'Status',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (agent) => {
          const activity = isActiveCategory ? (activities.get(agent.id) ?? 'working') : null;
          return activity ? (
            <AgentActivityChip activity={activity} agentId={agent.id} />
          ) : (
            <AgentStatusChip status={agent.status} agentId={agent.id} />
          );
        },
      },
      {
        header: 'User',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (agent) => {
          const user = agent.userId ? userMap.get(agent.userId) : null;
          return user ? (
            <Link
              to={ROUTES.userProfile(user.id)}
              className="flex items-center gap-1.5 hover:underline"
            >
              <UserAvatar
                avatarUrl={user.avatarUrl ?? undefined}
                userName={getUserDisplayName(user)}
                size="xs"
              />
              <span className="text-xs text-muted-foreground">{getUserDisplayName(user)}</span>
            </Link>
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          );
        },
      },
      {
        header: 'Repo',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (agent) => {
          const repo = getGitRepoAttachment(agent)?.repoSlug?.replace(
            /^https?:\/\/github\.com\//,
            '',
          );
          return repo ? (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <GitBranch className="h-3 w-3 shrink-0" />
              <span className="max-w-[200px] truncate">{repo}</span>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          );
        },
      },
      {
        header: isActiveCategory ? 'Running' : 'Created',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right',
        cell: (agent) => (
          <span className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {isActiveCategory ? formatDurationRange(agent.createdAt) : timeAgo(agent.createdAt)}
          </span>
        ),
      },
    ],
    [isActiveCategory, activities, userMap, orgRoutes],
  );

  return (
    <PageLayout maxWidth="5xl">
      <AsyncContent
        isLoading={isLoading}
        isError={isError}
        loadingMessage="Loading monitor…"
        errorMessage="Failed to load monitor data."
      >
        <PageHeader
          icon={<Activity className="h-5 w-5" />}
          title="Monitor"
          description="Real-time overview of agent activity across the system."
          actions={<AgentFilterBar filtersResult={agentFilters} agents={allNonArchived} />}
        />

        {/* Stats row */}
        <CardGrid cols={4}>
          <StatCard
            label="Active"
            value={categoryCounts.active.length}
            icon={<Activity className="h-5 w-5" />}
            valueClassName="text-status-success"
            selected={selectedCategory === 'active'}
            onClick={() => setSelectedCategory('active')}
          />
          <StatCard
            label="Needs Input"
            value={categoryCounts.needsInput.length}
            icon={<CirclePause className="h-5 w-5" />}
            valueClassName={
              categoryCounts.needsInput.length > 0 ? 'text-status-warning' : undefined
            }
            selected={selectedCategory === 'needs_input'}
            onClick={() => setSelectedCategory('needs_input')}
          />
          <StatCard
            label="Errored"
            value={categoryCounts.errored.length}
            icon={<AlertCircle className="h-5 w-5" />}
            valueClassName={categoryCounts.errored.length > 0 ? 'text-status-error' : undefined}
            selected={selectedCategory === 'errored'}
            onClick={() => setSelectedCategory('errored')}
          />
          <StatCard
            label="Completed"
            value={categoryCounts.completed.length}
            icon={<CheckCircle2 className="h-5 w-5" />}
            selected={selectedCategory === 'completed'}
            onClick={() => setSelectedCategory('completed')}
          />
        </CardGrid>

        {/* Agents table */}
        <Card>
          <CardHeader className="pb-3">
            <PanelTitle as="h2" size="sm" className="flex items-center gap-2">
              {isActiveCategory && (
                <span className="relative flex h-2 w-2">
                  {displayedAgents.length > 0 && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-status-success opacity-75" />
                  )}
                  <span
                    className={cn(
                      'relative inline-flex h-2 w-2 rounded-full',
                      displayedAgents.length > 0 ? 'bg-status-success' : 'bg-muted-foreground',
                    )}
                  />
                </span>
              )}
              {CATEGORY_LABELS[selectedCategory]}
              <span className="text-xs font-normal text-muted-foreground">
                ({displayedAgents.length})
              </span>
            </PanelTitle>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {displayedAgents.length === 0 ? (
              <EmptyState
                icon={<Bot className="h-5 w-5 text-muted-foreground" />}
                title={CATEGORY_EMPTY[selectedCategory]}
                className="py-10"
              />
            ) : (
              <DataTable<Agent>
                data={displayedAgents}
                columns={agentColumns}
                rowKey={(agent) => agent.id}
              />
            )}
          </CardContent>
        </Card>

        {isPlatformAdmin && <AdminUsagePanel snapshot={usageSnapshot} />}

        {/* Team activity */}
        <SectionCard title="Team Activity" headerClassName="pb-3">
          {userGroups.length === 0 ? (
            <EmptyState icon={<Activity className="h-5 w-5" />} title="No agent activity yet" />
          ) : (
            <div className="divide-y divide-border">
              {userGroups.map((group) => {
                const displayName = group.user ? getUserDisplayName(group.user) : 'Unknown';
                const recentAgent = group.agents.reduce((latest, a) =>
                  a.createdAt > latest.createdAt ? a : latest,
                );

                return (
                  <div
                    key={group.userId ?? 'unknown'}
                    className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                  >
                    <UserAvatar
                      avatarUrl={group.user?.avatarUrl ?? undefined}
                      userName={displayName}
                      size="md"
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {group.user ? (
                          <Link to={ROUTES.userProfile(group.user.id)} className="hover:underline">
                            <PanelTitle as="span" size="sm" className="mt-0">
                              {displayName}
                            </PanelTitle>
                          </Link>
                        ) : (
                          <PanelTitle as="span" size="sm" className="mt-0 text-muted-foreground">
                            {displayName}
                          </PanelTitle>
                        )}
                        {group.activeCount > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-status-success/15 px-2 py-0 text-3xs font-medium text-status-success">
                            <span className="h-1.5 w-1.5 rounded-full bg-status-success" />
                            {group.activeCount} active
                          </span>
                        )}
                      </div>
                      <PageDescription className="text-xs mt-0">
                        {group.agents.length} total agent{group.agents.length !== 1 ? 's' : ''}{' '}
                        &middot; latest {timeAgo(recentAgent.createdAt)}
                      </PageDescription>
                    </div>

                    <div className="hidden gap-1 sm:flex">
                      {group.agents
                        .filter((a) => ACTIVE_STATUSES.has(a.status))
                        .slice(0, 5)
                        .map((a) => (
                          <Link
                            key={a.id}
                            to={orgRoutes.agent(a.id)}
                            className="flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2 text-3xs transition-colors hover:bg-muted"
                            title={a.name}
                          >
                            <ActivityDot activity={activities.get(a.id) ?? 'working'} />
                            <span className="max-w-[100px] truncate">{a.name}</span>
                          </Link>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      </AsyncContent>
    </PageLayout>
  );
}
